import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useApp } from "../context/AppContext";
import PageSeo from "../components/PageSeo";
import html2canvas from "html2canvas";
import "./RiderProfile.css";

const ridingStyleConfig = {
  commuter: { label: "Commuter" },
  tourer: { label: "Tourer" },
  offroad: { label: "Off-road" },
  track: { label: "Track" },
  adventure: { label: "Adventure" },
};

const defaultStats = { rides: 0, totalKm: 0 };

const toInitials = (value = "") => {
  if (!value) return "MH";
  const parts = value.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() || "").join("") || "MH";
};

function SocialIcon({ type }) {
  if (type === "instagram") {
    return (
      <svg
        className="rider-social-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <path d="M17.5 6.5h.01" />
      </svg>
    );
  }

  return (
    <svg className="rider-social-icon" viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 15.5V8.5C10 8.22 10.22 8 10.5 8H13.5C13.78 8 14 8.22 14 8.5V15.5C14 15.78 13.78 16 13.5 16H10.5C10.22 16 10 15.78 10 15.5Z" />
      <path d="M8 10.5L5.5 9.5C5.2 9.38 5 9.08 5 8.75V8.25C5 7.8 5.45 7.48 5.88 7.65L8 8.5V10.5Z" />
      <path d="M16 10.5L18.5 9.5C18.8 9.38 19 9.08 19 8.75V8.25C19 7.8 18.55 7.48 18.12 7.65L16 8.5V10.5Z" />
      <path d="M8 13.5L5.5 14.5C5.2 14.62 5 14.92 5 15.25V15.75C5 16.2 5.45 16.52 5.88 16.35L8 15.5V13.5Z" />
      <path d="M16 13.5L18.5 14.5C18.8 14.62 19 14.92 19 15.25V15.75C19 16.2 18.55 16.52 18.12 16.35L16 15.5V13.5Z" />
    </svg>
  );
}

export default function RiderProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useApp();

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(defaultStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [toast, setToast] = useState("");
  const [shareState, setShareState] = useState({ downloading: false, error: "" });
  const shareCardRef = useRef(null);

  const canonicalPath = useMemo(() => `/riders/${username}`, [username]);

  useEffect(() => {
    const timeout = toast ? setTimeout(() => setToast(""), 2200) : null;
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [toast]);

  useEffect(() => {
    if (!username) return;

    const loadProfile = async () => {
      setLoading(true);
      setError("");

      const { data, error: profileError } = await supabase
        .from("profiles")
        .select("id, name, username, bio, city, state, avatar_url, instagram, youtube, riding_style, is_public, bikes_owned")
        .eq("username", username.trim().toLowerCase())
        .maybeSingle();

      if (profileError) {
        setError("Unable to load rider profile.");
        setLoading(false);
        return;
      }

      if (!data) {
        navigate("/riders", { replace: true });
        return;
      }

      setProfile(data);
      await Promise.all([refreshFollowers(data.id), refreshFollowing(data.id), refreshFollowState(data.id)]);
      setStats(defaultStats);
      setLoading(false);
    };

    loadProfile();
  }, [username, currentUser?.id, navigate]);

  const refreshFollowers = async (profileId) => {
    const { count } = await supabase.from("follows").select("id", { count: "exact", head: true }).eq("following_id", profileId);
    setFollowerCount(count || 0);
  };

  const refreshFollowing = async (profileId) => {
    const { count } = await supabase.from("follows").select("id", { count: "exact", head: true }).eq("follower_id", profileId);
    setFollowingCount(count || 0);
  };

  const refreshFollowState = async (profileId) => {
    if (!currentUser?.id || currentUser.id === profileId) {
      setIsFollowing(false);
      return;
    }
    const { data } = await supabase
      .from("follows")
      .select("id")
      .eq("follower_id", currentUser.id)
      .eq("following_id", profileId)
      .maybeSingle();
    setIsFollowing(Boolean(data));
  };

  const handleFollowToggle = async () => {
    if (!profile || !currentUser?.id || currentUser.id === profile.id) return;
    if (isFollowing) {
      await supabase.from("follows").delete().match({ follower_id: currentUser.id, following_id: profile.id });
      setIsFollowing(false);
      await refreshFollowers(profile.id);
      return;
    }
    await supabase.from("follows").insert({ follower_id: currentUser.id, following_id: profile.id });
    setIsFollowing(true);
    await refreshFollowers(profile.id);
  };

  const handleShare = async () => {
    if (!profile?.username) return;
    const shareUrl = `https://motohubindia.com/riders/${profile.username}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToast("Profile link copied");
    } catch {
      setToast("Copy failed. Try again.");
    }
  };

  const handleShareCard = async (action) => {
    if (!profile || shareState.downloading) return;
    setShareState({ downloading: true, error: "" });
    try {
      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: "#0f172a",
        scale: 2,
        logging: false,
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL("image/png");
      if (action === "download") {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${profile.username}-motohub.png`;
        link.click();
      } else if (action === "whatsapp") {
        const text = encodeURIComponent(
          `Check out ${profile.name}'s rider profile on MotoHub India: https://motohubindia.com/riders/${profile.username}`
        );
        window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
      }
      setShareState({ downloading: false, error: "" });
      setToast(action === "download" ? "Card downloaded" : "Opening WhatsApp...");
    } catch {
      setShareState({ downloading: false, error: "Unable to generate card." });
    }
  };

  const hasPrivateRestriction = useMemo(() => {
    if (!profile) return false;
    if (profile.is_public) return false;
    if (currentUser?.id === profile.id) return false;
    return !isFollowing;
  }, [profile, currentUser?.id, isFollowing]);

  const ridingStyleBadge = profile?.riding_style
    ? ridingStyleConfig[profile.riding_style] || { label: profile.riding_style }
    : null;

  const quickShareUrl = profile?.username ? `https://motohubindia.com/riders/${profile.username}` : "";

  return (
    <div className="rider-profile-page">
      <PageSeo
        title={profile ? `${profile.name} (@${profile.username}) | MotoHub India Rider` : "Rider Profile | MotoHub India"}
        description={
          profile
            ? `${profile.name} is a ${profile.riding_style || "passionate"} rider from ${profile.city || "India"}. Connect with riders on MotoHub India.`
            : "Discover rider profiles across India on MotoHub India."
        }
        keywords={["MotoHub India rider", "rider profile", profile?.city || "India", profile?.riding_style || "rider"]}
        path={canonicalPath}
        image="/og-image.jpg"
      />

      <div className="rider-profile-shell">
        {toast ? <div className="rider-toast">{toast}</div> : null}

        {loading ? (
          <div className="rider-loading">Loading rider...</div>
        ) : error ? (
          <div className="rider-error">{error}</div>
        ) : profile ? (
          <div className="rider-profile-layout">
            <div className="rider-hidden-card-wrap" aria-hidden="true">
              <div ref={shareCardRef} className="rider-hidden-card">
                <div className="rider-hidden-top">
                  {profile.avatar_url ? <img src={profile.avatar_url} alt={profile.name} /> : <div>{toInitials(profile.name)}</div>}
                  <div>
                    <p>{profile.name}</p>
                    <span>@{profile.username}</span>
                  </div>
                </div>
                <p>{profile.bio?.slice(0, 90) || "Ride. Explore. Connect."}</p>
              </div>
            </div>

            <section className="rider-main-card">
              <div className="rider-head">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt={profile.name} className="rider-avatar-lg" />
                ) : (
                  <div className="rider-avatar-lg rider-avatar-lg-fallback">{toInitials(profile.name)}</div>
                )}
                <div className="rider-head-meta">
                  <h1>{profile.name}</h1>
                  <p className="rider-username">@{profile.username}</p>
                  <p className="rider-location">
                    {profile.city ? `${profile.city}${profile.state ? `, ${profile.state}` : ""}` : "Across India"}
                  </p>
                  {ridingStyleBadge ? <span className="rider-style-chip">{ridingStyleBadge.label}</span> : null}
                </div>
              </div>

              <p className="rider-bio">{profile.bio || "This rider has not added a bio yet."}</p>

              {profile.bikes_owned?.length ? (
                <div className="rider-bikes">
                  {profile.bikes_owned.map((bike) => (
                    <span key={bike}>{bike}</span>
                  ))}
                </div>
              ) : null}

              <div className="rider-stats-grid">
                <StatCard label="Followers" value={followerCount} />
                <StatCard label="Following" value={followingCount} />
                <StatCard label="Rides" value={stats.rides} />
                <StatCard label="Total KM" value={stats.totalKm} suffix=" km" />
              </div>

              <div className="rider-social-row">
                {profile.instagram ? (
                  <a href={profile.instagram} target="_blank" rel="noopener noreferrer" className="rider-social-btn">
                    <SocialIcon type="instagram" />
                    Instagram
                  </a>
                ) : null}
                {profile.youtube ? (
                  <a href={profile.youtube} target="_blank" rel="noopener noreferrer" className="rider-social-btn">
                    <SocialIcon type="youtube" />
                    YouTube
                  </a>
                ) : null}
              </div>

              <div className="rider-action-row">
                {currentUser?.id && currentUser.id !== profile.id ? (
                  <button onClick={handleFollowToggle} className={`rider-action-btn ${isFollowing ? "muted" : ""}`} type="button">
                    {isFollowing ? "Following" : "Follow"}
                  </button>
                ) : null}
                <button onClick={handleShare} className="rider-action-btn muted" type="button">
                  Copy Profile Link
                </button>
                <button onClick={() => handleShareCard("download")} className="rider-action-btn muted" disabled={shareState.downloading} type="button">
                  Download Card
                </button>
                <button onClick={() => handleShareCard("whatsapp")} className="rider-action-btn muted" disabled={shareState.downloading} type="button">
                  Share on WhatsApp
                </button>
              </div>
              {shareState.error ? <p className="rider-inline-error">{shareState.error}</p> : null}
            </section>

            <aside className="rider-side-card">
              <h2>Profile Actions</h2>
              <a href={quickShareUrl} target="_blank" rel="noopener noreferrer" className="rider-side-link">
                Open Public URL
              </a>
              {profile.instagram ? (
                <a href={profile.instagram} target="_blank" rel="noopener noreferrer" className="rider-side-link">
                  Open Instagram
                </a>
              ) : null}
              {profile.youtube ? (
                <a href={profile.youtube} target="_blank" rel="noopener noreferrer" className="rider-side-link">
                  Open YouTube
                </a>
              ) : null}
              <button onClick={handleShare} className="rider-side-btn" type="button">
                Copy Link
              </button>
            </aside>

            <section className="rider-content-card">
              {hasPrivateRestriction ? (
                <div className="rider-private-box">
                  <h3>This profile is private</h3>
                  <p>Follow this rider to unlock shared updates and community notes.</p>
                </div>
              ) : (
                <div className="rider-content-sections">
                  <div>
                    <h3>Ride Highlights</h3>
                    <p>Ride logs and highlights will appear here once this rider shares adventures.</p>
                  </div>
                  <div>
                    <h3>Community Notes</h3>
                    <p>Stay tuned for route suggestions, ride clips, and rider recommendations.</p>
                  </div>
                </div>
              )}
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix = "" }) {
  return (
    <div className="rider-stat-card">
      <p>{label}</p>
      <strong>
        {value}
        {suffix}
      </strong>
    </div>
  );
}
